import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const PaymentSuccess = () => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        // Get order ID from location state or URL params
        const orderId = location.state?.orderId;
        
        if (!orderId) {
          setError('Order information not found');
          setLoading(false);
          return;
        }
        
        // Get token from localStorage
        const token = localStorage.getItem('token');
        
        if (!token) {
          navigate('/login');
          return;
        }
        
        // Fetch order details
        const res = await axios.get(`/api/orders/${orderId}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        setOrder(res.data);
        
        // Verify payment status
        await axios.post('/api/payments/verify', 
          { orderId },
          {
            headers: {
              'Content-Type': 'application/json',
              'x-auth-token': token
            }
          }
        );
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details');
        setLoading(false);
      }
    };
    
    fetchOrderDetails();
  }, [location.state, navigate]);
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="neumorph p-8 rounded-full">
          <svg className="animate-spin h-8 w-8 text-gray-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }
  
  if (error || !order) {
    return (
      <div className="text-center py-8">
        <div className="neumorph p-6 rounded-lg inline-block">
          <h3 className="text-xl text-red-600 mb-2">Error</h3>
          <p className="text-gray-700">{error || 'Order not found'}</p>
          <Link to="/dashboard" className="neumorph-btn mt-4 px-4 py-2 inline-block">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-2xl mx-auto">
      <div className="neumorph p-8 rounded-lg text-center">
        <div className="mb-6">
          <div className="neumorph inline-flex items-center justify-center p-4 rounded-full bg-green-100 text-green-600">
            <svg className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        
        <h1 className="text-3xl font-bold mb-4">Payment Successful!</h1>
        
        <p className="text-gray-600 mb-6">
          Thank you for your purchase. Your order has been successfully placed and is now being processed.
        </p>
        
        <div className="neumorph-inset p-6 rounded-lg mb-6">
          <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
          
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Order ID:</span>
            <span className="font-medium">{order._id}</span>
          </div>
          
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Date:</span>
            <span className="font-medium">{new Date(order.createdAt).toLocaleDateString()}</span>
          </div>
          
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Payment Method:</span>
            <span className="font-medium">{order.paymentMethod.toUpperCase()}</span>
          </div>
          
          <div className="flex justify-between mb-2">
            <span className="text-gray-600">Status:</span>
            <span className="font-medium capitalize">{order.status}</span>
          </div>
          
          <div className="flex justify-between">
            <span className="text-gray-600">Total Amount:</span>
            <span className="font-bold">${order.totalAmount.toFixed(2)}</span>
          </div>
        </div>
        
        <div className="flex justify-center space-x-4">
          <Link to="/dashboard" className="neumorph-btn px-6 py-3">
            View Orders
          </Link>
          <Link to="/products" className="neumorph-btn px-6 py-3">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
